import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Modal, Form, Row, Col } from 'react-bootstrap';
import { FiCreditCard, FiRefreshCw, FiEye, FiEdit, FiTrash2 } from 'react-icons/fi';
import { AlertTriangle, Download, RefreshCw, Upload } from 'lucide-react';
import { Button as OrakleButton } from '../../components/ui';
import { fetchTripsPoliciesRequest, changePolicyStatusRequest } from '../../store/actions/tripsActions';
import { deleteActivityPolicyRequest } from '../../store/actions/policiesActions';
import { fetchPartnersRequest } from '../../store/actions/partnerActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsPolicies.css';

const EMPTY_TRIPS_POLICIES_FILTERS = {
    policy_code: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
    client_email: '',
    company_name: '',
    status: '',
    trigger_type: '',
    activity_start_date_from: '',
    activity_start_date_to: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    trip_modified: '',
    new_trip_start_date_from: '',
    new_trip_start_date_to: '',
    new_trip_end_date_from: '',
    new_trip_end_date_to: '',
};

const TRIP_POLICY_STATUS_OPTIONS = [
    'Created',
    'CG confirmed',
    'Paid',
    'Cancelled',
    'RIB Received',
];

const TRIP_TRIGGER_TYPE_OPTIONS = [
    { value: 'forecast', label: 'Forecast' },
    { value: 'observation', label: 'Observation' },
    { value: 'forecast and observation', label: 'Forecast and Observation' },
];

const appendActiveFiltersToParams = (params, activeFilters) => {
    Object.keys(activeFilters).forEach((key) => {
        const value = activeFilters[key];
        if (key === 'trip_modified') {
            if (value === 'true' || value === 'false') {
                params[key] = value;
            }
            return;
        }
        if (value != null && String(value).trim() !== '') {
            params[key] = String(value).trim();
        }
    });
};

const AdminTripsPolicies = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const policiesState = useSelector((state) => state.trips.policies);
    const { changePolicyStatus } = useSelector((state) => state.trips);
    const { deletePolicy } = useSelector((state) => state.policies);
    const { partners } = useSelector((state) => state.partners);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('trips-policies', EMPTY_TRIPS_POLICIES_FILTERS, {
        persistWhen: isAuthenticated,
        withCompanyPartnerSearch: true,
    });
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [successMessage, setSuccessMessage] = useState(null);
    const [filterPartners, setFilterPartners] = useState([]);
    const [filterPartnersLoading, setFilterPartnersLoading] = useState(false);
    const [filterPartnersError, setFilterPartnersError] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = filterPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [filterPartners]);

    const getDeep = (obj, path, fallback = 'N/A') => {
        try {
            const val = path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
            return val ?? fallback;
        } catch {
            return fallback;
        }
    };

    const handleDetails = (item) => {
        const policyId = getDeep(item, ['policy_info', 'policy_id'], null);
        if (policyId) {
            navigate(`/admin/trips/policies/${policyId}`);
        }
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        const currentStatus = getDeep(item, ['policy_info', 'status'], '');
        setSelectedStatus(currentStatus);
        setSuccessMessage(null);
        setShowEditModal(true);
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
    };

    const handleSaveStatus = () => {
        if (selectedItem && selectedStatus) {
            const policyId = getDeep(selectedItem, ['policy_info', 'policy_id'], null);
            if (policyId) {
                dispatch(changePolicyStatusRequest(policyId, selectedStatus));
            }
        }
    };

    const handleDelete = (item) => {
        setSelectedItem(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (!selectedItem) return;
        const policyId = getDeep(selectedItem, ['policy_info', 'policy_id'], null);
        if (policyId) {
            dispatch(deleteActivityPolicyRequest(policyId));
        }
    };

    // Charger les polices avec pagination et filtres
    const loadPolicies = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        appendActiveFiltersToParams(params, activeFilters);

        dispatch(fetchTripsPoliciesRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadPolicies(currentPage);
            // Charger tous les partenaires (sans pagination)
            if (!partners.partners?.data) {
                dispatch(fetchPartnersRequest({})); // Passer un objet vide pour obtenir tous les partenaires
            }
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        const loadFilterPartners = async () => {
            try {
                setFilterPartnersLoading(true);
                setFilterPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setFilterPartners(partnersArray);
            } catch (error) {
                setFilterPartners([]);
                setFilterPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setFilterPartnersLoading(false);
            }
        };

        if (isAuthenticated) {
            loadFilterPartners();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        loadPolicies(currentPage);
    }, [currentPage]);

    // Extraire les données et métadonnées de la réponse API
    const policiesData = policiesState?.data?.data || policiesState?.data?.policies || policiesState?.data || [];
    const policiesArray = Array.isArray(policiesData) ? policiesData : [];
    const meta = policiesState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + policiesArray.length, totalItems);

    const handleRefresh = () => {
        loadPolicies(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = { ...filters, company_name: companyName };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        loadPolicies(1, nextFilters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_TRIPS_POLICIES_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadPolicies(1, emptyFilters);
        }, 100);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les polices de l'API (pas de filtrage côté client)
    const currentPolicies = policiesArray;

    useEffect(() => {
        if (changePolicyStatus.success) {
            setSuccessMessage('Statut modifié avec succès !');
            // Recharger les données
            loadPolicies(currentPage);
            // Fermer le modal après un délai
            const timer = setTimeout(() => {
                setShowEditModal(false);
                setSuccessMessage(null);
                setSelectedItem(null);
                setSelectedStatus('');
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [changePolicyStatus.success, currentPage, loadPolicies]);

    useEffect(() => {
        if (deletePolicy.success) {
            setSuccessMessage('Police supprimée avec succès');
            setShowDeleteModal(false);
            setSelectedItem(null);
            loadPolicies(currentPage);
            const timer = setTimeout(() => setSuccessMessage(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [deletePolicy.success, currentPage, loadPolicies]);

    const getField = (obj, keys, fallback = 'N/A') => { for (const k of keys) { if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k]; } return fallback; };

    return (
        <div className="dashboard-page admin-trips-policies-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCreditCard className="me-2" />
                                Trip - Gestion Polices
                            </h2>
                            <p className="text-muted mb-0">Liste des séjours - Polices liées aux séjours</p>
                        </div>
                    </div>

                    {policiesState.error && (
                        <Alert variant="danger" className="mt-3">
                            {policiesState.error}
                        </Alert>
                    )}
                    {successMessage && (
                        <Alert variant="success" className="mt-3" dismissible onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    <div className="orakle-btn-toolbar" role="toolbar" aria-label="Actions sur les polices séjours">
                        <OrakleButton
                            variant="primary"
                            icon={Download}
                            onClick={() => navigate('/admin/trips/policies/export', { state: { filters: { ...appliedFilters } } })}
                        >
                            Export Excel — Polices
                        </OrakleButton>
                        <OrakleButton
                            variant="info"
                            icon={RefreshCw}
                            onClick={() => navigate('/admin/trips/policies/modified-import')}
                        >
                            Importer Modifications
                        </OrakleButton>
                        <OrakleButton
                            variant="warning"
                            icon={AlertTriangle}
                            onClick={() => navigate('/admin/trips/policies/cancelled-import')}
                        >
                            Importer Polices Annulées
                        </OrakleButton>
                        <OrakleButton
                            variant="success"
                            icon={Upload}
                            emphasis
                            onClick={() => navigate('/admin/trips/policies/import')}
                        >
                            Importer polices
                        </OrakleButton>
                    </div>

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: 301826051"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_first_name"
                                            value={filters.client_first_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Prénom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_last_name"
                                            value={filters.client_last_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_email"
                                            value={filters.client_email ?? ''}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Nom de société</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={filters.company_name}
                                        onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                        onInputChange={setCompanyPartnerSearch}
                                        options={partnerCompanyOptions}
                                        loading={filterPartnersLoading}
                                        error={filterPartnersError}
                                        onKeyDown={handleKeyPress}
                                        placeholder="Rechercher un partenaire…"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Statut</Form.Label>
                                        <Form.Select
                                            name="status"
                                            value={filters.status}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">Tous les statuts</option>
                                            {TRIP_POLICY_STATUS_OPTIONS.map((status) => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type de déclenchement</Form.Label>
                                        <Form.Select
                                            name="trigger_type"
                                            value={filters.trigger_type}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">Tous les types</option>
                                            {TRIP_TRIGGER_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début activité (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date_from"
                                            value={filters.activity_start_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début activité (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date_to"
                                            value={filters.activity_start_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_from"
                                            value={filters.activity_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_to"
                                            value={filters.activity_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Dates séjour modifiées</Form.Label>
                                        <Form.Select
                                            name="trip_modified"
                                            value={filters.trip_modified}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">Tous</option>
                                            <option value="true">Oui</option>
                                            <option value="false">Non</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouveau début séjour (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="new_trip_start_date_from"
                                            value={filters.new_trip_start_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouveau début séjour (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="new_trip_start_date_to"
                                            value={filters.new_trip_start_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouvelle fin séjour (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="new_trip_end_date_from"
                                            value={filters.new_trip_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouvelle fin séjour (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="new_trip_end_date_to"
                                            value={filters.new_trip_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={policiesState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${policiesState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {policiesState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des polices (trips)...</p>
                        </div>
                    ) : policiesState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{policiesState.error}</Alert>
                        </div>
                    ) : currentPolicies.length === 0 ? (
                        <div className="text-center py-5">
                            <FiCreditCard size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune police disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Code police</th>
                                            <th>Client</th>
                                            <th>Email</th>
                                            <th>Téléphone</th>
                                            <th>Partenaire</th>
                                            <th>N° réservation</th>
                                            <th>Début</th>
                                            <th>Fin</th>
                                            <th>Nouveau début</th>
                                            <th>Nouvelle fin</th>
                                            <th>Statut</th>
                                            <th className="text-end col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentPolicies) && currentPolicies.length > 0 ? (
                                            currentPolicies.map((item, idx) => (
                                                <tr key={getDeep(item, ['policy_info', 'policy_id'], idx)}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(item, ['policy_info', 'policy_code'], '-')}</td>
                                                    <td>{`${getDeep(item, ['policy_info', 'client_info', 'first_name'], '')} ${getDeep(item, ['policy_info', 'client_info', 'last_name'], '')}`.trim() || 'N/A'}</td>
                                                    <td>{getDeep(item, ['policy_info', 'client_info', 'email'])}</td>
                                                    <td>{getDeep(item, ['policy_info', 'client_info', 'phone_number'])}</td>
                                                    <td>{getDeep(item, ['policy_info', 'partner_info', 'company_name'])}</td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {getDeep(item, ['policy_info', 'activity_info', 'reservation_number'], 'N/A')}
                                                    </td>
                                                    <td>{getDeep(item, ['policy_info', 'activity_info', 'activity_start_date'], getDeep(item, ['policy_info', 'activity_info', 'trip_effective_start_date']))}</td>
                                                    <td>{getDeep(item, ['policy_info', 'activity_info', 'activity_end_date'], getDeep(item, ['policy_info', 'activity_info', 'trip_effective_end_date']))}</td>
                                                    <td>{getDeep(item, ['policy_info', 'activity_info', 'new_trip_start_date'], '—')}</td>
                                                    <td>{getDeep(item, ['policy_info', 'activity_info', 'new_trip_end_date'], '—')}</td>
                                                    <td>{getDeep(item, ['policy_info', 'status'])}</td>
                                                    <td className="text-end col-actions">
                                                        <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleDetails(item)} title="Détails" aria-label="Détails">
                                                            <FiEye />
                                                        </Button>
                                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEdit(item)} title="Modifier" aria-label="Modifier">
                                                            <FiEdit />
                                                        </Button>
                                                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item)} title="Supprimer" aria-label="Supprimer">
                                                            <FiTrash2 />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="12" className="text-center">
                                                    Aucune police trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} police{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadPolicies(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadPolicies(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadPolicies(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadPolicies(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadPolicies(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadPolicies(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadPolicies(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                    {/* Modals */}
                    <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>Détails de la police</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(selectedItem, null, 2)}</pre>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Fermer</Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal show={showEditModal} onHide={() => {
                        setShowEditModal(false);
                        setSelectedItem(null);
                        setSelectedStatus('');
                        setSuccessMessage(null);
                    }}>
                        <Modal.Header closeButton>
                            <Modal.Title>Modifier le statut de la police</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {successMessage && (
                                <Alert variant="success" className="mb-3">
                                    {successMessage}
                                </Alert>
                            )}
                            {changePolicyStatus.error && (
                                <Alert variant="danger" className="mb-3">
                                    {changePolicyStatus.error}
                                </Alert>
                            )}
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Code police</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={getDeep(selectedItem, ['policy_info', 'policy_code'], 'N/A')}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Statut actuel</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={getDeep(selectedItem, ['policy_info', 'status'], 'N/A')}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nouveau statut *</Form.Label>
                                    <Form.Select
                                        value={selectedStatus}
                                        onChange={handleStatusChange}
                                        disabled={changePolicyStatus.loading}
                                    >
                                        <option value="">Sélectionner un statut</option>
                                        {TRIP_POLICY_STATUS_OPTIONS.map((status) => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowEditModal(false);
                                    setSelectedItem(null);
                                    setSelectedStatus('');
                                    setSuccessMessage(null);
                                }}
                                disabled={changePolicyStatus.loading}
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSaveStatus}
                                disabled={changePolicyStatus.loading || !selectedStatus || selectedStatus === getDeep(selectedItem, ['policy_info', 'status'], '')}
                            >
                                {changePolicyStatus.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Modification...
                                    </>
                                ) : (
                                    'Enregistrer'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal
                        show={showDeleteModal}
                        onHide={() => {
                            if (!deletePolicy.loading) {
                                setShowDeleteModal(false);
                                setSelectedItem(null);
                            }
                        }}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Confirmer la suppression</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {deletePolicy.error && (
                                <Alert variant="danger" className="mb-3">
                                    {deletePolicy.error}
                                </Alert>
                            )}
                            Êtes-vous sûr de vouloir supprimer la police avec le code{' '}
                            <strong>{getDeep(selectedItem, ['policy_info', 'policy_code'], 'N/A')}</strong> ?
                            <br />
                            <small className="text-muted">La ressource associée à cette police sera supprimée sur le serveur. Cette action est irréversible.</small>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedItem(null);
                                }}
                                disabled={deletePolicy.loading}
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="danger"
                                onClick={confirmDelete}
                                disabled={deletePolicy.loading}
                            >
                                {deletePolicy.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Suppression...
                                    </>
                                ) : (
                                    'Supprimer'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsPolicies;


